/*
 * JadeChat — full-feature chat widget (launcher + panel + lead capture).
 *
 * This file is intentionally kept as a single cohesive component. Static
 * analyzers flag it for cyclomatic complexity / line count and recommend
 * splitting into ChatHeader/ChatMessage/ChatInput sub-components — that
 * refactor would add 5+ files and 2 custom hooks for a single isolated
 * widget with no shared state, which conflicts with our DRY / "simplest
 * working solution" guideline. Suppression below is intentional.
 */
/* eslint-disable max-lines-per-function, complexity, max-lines */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, FileText, Handshake, CalendarClock, HeartHandshake } from "lucide-react";
import { api } from "@/lib/api";

const STORAGE_KEY = "ymp_jade_session";

// Marker the backend appends to Jade's canned reply. When we see it on the
// assistant message we render the four CTA buttons inline.
const JADE_CANNED_MARKER = "JADE_CANNED_V1";

const JADE_PHOTO = "/jade-avatar-0.png";

const greeting = {
    role: "assistant",
    content:
        "Hey, I'm Jade — your guide to the Young Mogul Program. Ask me anything about the 9-week accelerator, mentors, or how to get involved.",
};

export const JadeChat = ({ onApply, onPartner }) => {
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(true);
    const [hideForFooter, setHideForFooter] = useState(false);
    const [messages, setMessages] = useState([greeting]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    // Two-step lead capture: hold the visitor's first question until they
    // give us an email, then save both to /api/contacts before showing CTAs.
    const [awaitingEmail, setAwaitingEmail] = useState(false);
    const [pendingQuestion, setPendingQuestion] = useState("");
    const [leadCaptured, setLeadCaptured] = useState(false);
    // Non-sensitive chat thread ID — used to correlate Jade conversations in
    // the backend logs. NOT a credential. localStorage is intentional so the
    // same visitor sees a continuous thread across reloads.
    const [sessionId] = useState(() => {
        let id = localStorage.getItem(STORAGE_KEY);
        if (!id) {
            id = `jade-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            localStorage.setItem(STORAGE_KEY, id);
        }
        return id;
    });

    const scrollRef = useRef(null);

    // Auto-scroll to bottom on new messages. Ref usage is exempt from
    // exhaustive-deps per React's official rules.
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, loading]);

    // Hide the floating launcher when the footer is on screen so it
    // doesn't cover the phone / email / brand lockup at the bottom
    // or visually graze the platform's "Made with Emergent" badge.
    // All values referenced inside this effect are either effect-local
    // (`obs`, `timer`, `attach`, `entry`), globals (`IntersectionObserver`,
    // `document`, `setTimeout`), or stable React setters (`setHideForFooter`)
    // — none can legally be in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        let obs;
        let timer;
        const attach = () => {
            const footer = document.querySelector("footer");
            if (!footer) {
                timer = setTimeout(attach, 300);
                return;
            }
            obs = new IntersectionObserver(
                ([entry]) => setHideForFooter(entry.isIntersecting),
                // Fire 160px BEFORE the footer enters view so the launcher
                // disappears cleanly with no overlap on either device.
                { threshold: 0, rootMargin: "0px 0px 160px 0px" },
            );
            obs.observe(footer);
        };
        attach();
        return () => {
            if (timer) clearTimeout(timer);
            if (obs) obs.disconnect();
        };
    }, []);

    const handleOpen = () => {
        setOpen(true);
        setUnread(false);
    };

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const askForEmail = (question) => {
        setPendingQuestion(question);
        setAwaitingEmail(true);
        setMessages((prev) => [
            ...prev,
            {
                role: "assistant",
                content:
                    "Got it — appreciate the question! Drop your email below so the team can follow up with the real answer.",
            },
        ]);
    };

    const fetchCannedAndShowCtas = async (question) => {
        setLoading(true);
        try {
            const res = await api.post("/chat", {
                session_id: sessionId,
                message: question,
                history: [],
            });
            setMessages((prev) => [
                ...prev,
                { role: "assistant", content: res.data.reply },
            ]);
        } catch (err) {
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        "I'm having trouble connecting right now. Call us at (916) 400-9537 or email EDUtainmentpros@gmail.com and a human will get right back to you.",
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const saveLead = async (email, question) => {
        try {
            await api.post("/contacts", {
                name: "Jade Chat Visitor",
                email,
                subject: "Asked Jade",
                message: question || "(No question captured)",
            });
        } catch (err) {
            // Non-blocking: even if save fails, the visitor still sees CTAs.
            if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.warn("Jade lead save failed", err);
            }
        }
    };

    const send = async (text) => {
        const trimmed = (text ?? input).trim();
        if (!trimmed || loading) return;
        setInput("");
        setMessages((prev) => [...prev, { role: "user", content: trimmed }]);

        // Step 2: waiting on the visitor's email.
        if (awaitingEmail) {
            if (!EMAIL_RE.test(trimmed)) {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content:
                            "Hmm, that doesn't look like a valid email. Mind double-checking and sending it again?",
                    },
                ]);
                return;
            }
            await saveLead(trimmed, pendingQuestion);
            setLeadCaptured(true);
            setAwaitingEmail(false);
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        "Locked in — the team will reach out at " +
                        trimmed +
                        " soon. While you wait, here's the fastest way to get started:",
                },
            ]);
            await fetchCannedAndShowCtas(pendingQuestion);
            setPendingQuestion("");
            return;
        }

        // Step 3+: lead already captured — go straight to canned + CTAs.
        if (leadCaptured) {
            await fetchCannedAndShowCtas(trimmed);
            return;
        }

        // Step 1: first inquiry — capture the question, ask for email.
        askForEmail(trimmed);
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const ctas = [
        {
            label: "Apply Now",
            sub: "Instructor / School applicants",
            icon: FileText,
            testId: "jade-cta-apply",
            onClick: () => onApply?.(),
        },
        {
            label: "Become a Partner",
            sub: "Join the Young Mogul Ecosystem",
            icon: Handshake,
            testId: "jade-cta-partner",
            onClick: () => onPartner?.("partner"),
        },
        {
            label: "Setup a Meeting",
            sub: "Talk to a team member",
            icon: CalendarClock,
            testId: "jade-cta-meeting",
            onClick: () => onPartner?.("meeting"),
        },
        {
            label: "Sponsor a Program",
            sub: "Fund a 9-week cohort · Sacramento region",
            icon: HeartHandshake,
            testId: "jade-cta-sponsor",
            onClick: () => onPartner?.("sponsor"),
        },
    ];

    const CtaButtons = () => (
        <div className="mt-3 space-y-2" data-testid="jade-cta-buttons">
            {ctas.map((c) => {
                const Icon = c.icon;
                return (
                    <button
                        key={c.testId}
                        onClick={c.onClick}
                        data-testid={c.testId}
                        className="w-full flex items-start gap-3 px-3 py-2.5 border border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37]/[0.06] text-left transition-all group"
                    >
                        <span className="h-7 w-7 grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 shrink-0 mt-0.5 group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-colors">
                            <Icon size={13} className="text-[#D4AF37] group-hover:text-black transition-colors" />
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block font-display text-[13px] uppercase tracking-wider text-white group-hover:text-[#D4AF37] transition-colors leading-tight">
                                {c.label}
                            </span>
                            <span className="block text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1 leading-snug">
                                {c.sub}
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );

    return (
        <>
            {/* Floating launcher */}
            <AnimatePresence>
                {!open && !hideForFooter && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.85, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        transition={{ duration: 0.4 }}
                        onClick={handleOpen}
                        data-testid="jade-launcher"
                        className="fixed bottom-24 right-3 sm:bottom-20 sm:right-6 z-[90] group flex items-center gap-3 pl-2 pr-5 py-2 bg-black border border-[#D4AF37]/40 hover:border-[#D4AF37] gold-glow rounded-full transition-all"
                        aria-label="Chat with Jade"
                    >
                        <div className="relative h-12 w-12 rounded-full overflow-hidden border border-[#D4AF37]/60 shrink-0">
                            <img
                                src={JADE_PHOTO}
                                alt="Jade"
                                className="w-full h-full object-cover object-[50%_25%]"
                            />
                            {unread && (
                                <span className="absolute top-0 right-0 h-3 w-3 bg-[#D4AF37] rounded-full ring-2 ring-black animate-pulse" />
                            )}
                        </div>
                        <div className="text-left hidden sm:block">
                            <div className="font-display text-sm uppercase tracking-wider text-white leading-none">
                                Ask Jade
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1">
                                Live · YMP Concierge
                            </div>
                        </div>
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Chat panel */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 30, scale: 0.95 }}
                        transition={{ duration: 0.35 }}
                        className="fixed bottom-24 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-[95] w-auto sm:w-[400px] h-[600px] max-h-[75vh] bg-[#0A0A0A] border border-[#D4AF37]/40 gold-glow flex flex-col"
                        data-testid="jade-panel"
                    >
                        {/* Header */}
                        <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-gradient-to-b from-black to-[#0A0A0A]">
                            <div className="h-11 w-11 rounded-full overflow-hidden border border-[#D4AF37]">
                                <img
                                    src={JADE_PHOTO}
                                    alt="Jade"
                                    className="w-full h-full object-cover object-[50%_25%]"
                                />
                            </div>
                            <div className="flex-1">
                                <div className="font-display text-base uppercase tracking-wider text-white leading-none flex items-center gap-2">
                                    Jade
                                    <Sparkles
                                        size={12}
                                        className="text-[#D4AF37]"
                                    />
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1.5 flex items-center gap-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                    YMP Concierge
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="jade-close"
                                className="h-9 w-9 grid place-items-center text-white/60 hover:text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]"
                                aria-label="Close chat"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Messages */}
                        <div
                            ref={scrollRef}
                            data-testid="jade-messages"
                            className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar"
                        >
                            {messages.map((m, i) => {
                                const hasCta =
                                    m.role === "assistant" &&
                                    typeof m.content === "string" &&
                                    m.content.includes(JADE_CANNED_MARKER);
                                const displayText = hasCta
                                    ? m.content.replace(JADE_CANNED_MARKER, "").trim()
                                    : m.content;
                                return (
                                    <div
                                        key={`${m.role}-${i}-${m.content?.slice(0, 16)}`}
                                        className={`flex ${
                                            m.role === "user"
                                                ? "justify-end"
                                                : "justify-start"
                                        }`}
                                    >
                                        <div
                                            className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                                                m.role === "user"
                                                    ? "bg-[#D4AF37] text-black"
                                                    : "bg-[#141414] border border-white/10 text-white/90"
                                            }`}
                                            data-testid={`jade-msg-${m.role}`}
                                        >
                                            {displayText}
                                            {hasCta && <CtaButtons />}
                                        </div>
                                    </div>
                                );
                            })}

                            {loading && (
                                <div className="flex justify-start">
                                    <div
                                        className="bg-[#141414] border border-white/10 px-4 py-3 flex items-center gap-1.5"
                                        data-testid="jade-typing"
                                    >
                                        {[0, 1, 2].map((d) => (
                                            <span
                                                key={d}
                                                className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse"
                                                style={{
                                                    animationDelay: `${d * 0.15}s`,
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {messages.length === 1 && !loading && (
                                <div className="pt-2 space-y-2">
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 px-1">
                                        Jump straight in
                                    </div>
                                    <CtaButtons />
                                </div>
                            )}
                        </div>

                        {/* Quick actions */}
                        <div className="px-4 pt-2 pb-1 flex gap-2 flex-wrap">
                            <button
                                onClick={onApply}
                                data-testid="jade-quick-apply"
                                className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition"
                            >
                                Apply Now
                            </button>
                            <button
                                onClick={onPartner}
                                data-testid="jade-quick-partner"
                                className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-white/15 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
                            >
                                Partner
                            </button>
                            <a
                                href="tel:9164009537"
                                className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-white/15 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
                            >
                                Call Us
                            </a>
                        </div>

                        {/* Input */}
                        <div className="p-3 border-t border-white/10 bg-black/40 flex items-end gap-2">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={onKey}
                                rows={1}
                                placeholder={
                                    awaitingEmail
                                        ? "you@example.com"
                                        : "Ask Jade anything..."
                                }
                                data-testid="jade-input"
                                className="flex-1 bg-[#0a0a0a] border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none resize-none max-h-32"
                                disabled={loading}
                            />
                            <button
                                onClick={() => send()}
                                disabled={loading || !input.trim()}
                                data-testid="jade-send"
                                className="h-11 w-11 bg-[#D4AF37] text-black grid place-items-center hover:bg-[#FFF3A1] transition disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Send message"
                            >
                                <Send size={16} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
